import { useMemo, type ReactNode } from 'react'
import { GlobalDateContext, type GlobalDateRange } from './globalDateContextStore'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { computeGlobalPresetRange } from '../lib/datePresets'

const DATE_RANGE_STORAGE_KEY = 'vaultbi-date-range'

function isValidIsoDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(new Date(s).getTime())
}

function validateDateRange(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false
  const obj = value as Record<string, unknown>
  if (!('startDate' in obj) || !('endDate' in obj)) return false
  if (typeof obj.startDate !== 'string' || typeof obj.endDate !== 'string') return false

  const isAllTime = obj.preset === 'allTime' && obj.startDate === '' && obj.endDate === ''
  const hasDates = isValidIsoDate(obj.startDate) && isValidIsoDate(obj.endDate)
  return isAllTime || hasDates
}

const defaultDateRange = (): GlobalDateRange => {
  const { start, end } = computeGlobalPresetRange('last30d')
  return { startDate: start, endDate: end, preset: 'last30d' }
}

export function GlobalDateProvider({ children }: { children: ReactNode }) {
  const [dateRange, setDateRange] = useLocalStorage<GlobalDateRange>(
    DATE_RANGE_STORAGE_KEY,
    defaultDateRange(),
    {
      serialize: JSON.stringify,
      deserialize: JSON.parse,
      validate: validateDateRange,
    },
  )

  const value = useMemo(
    () => ({
      dateRange,
      setDateRange,
    }),
    [dateRange, setDateRange],
  )

  return (
    <GlobalDateContext.Provider value={value}>
      {children}
    </GlobalDateContext.Provider>
  )
}
